import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import MaskAndValid from "utils/maskAndValid";

export default function GridModal({
  open,
  onClose,
  onSave,
  currentBrand,
  activeFamilyKey,
  families,
  tempGridState,
  setTempGridState,
  applyBulkQuantity,
  selectedBulkColors,
  selectedBulkProducts,
  toggleBulkSelection,
  theme,
}) {
  const [bulkQty, setBulkQty] = useState("");
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [selectedMatrixCells, setSelectedMatrixCells] = useState(new Set());

  // Inicializar todos os checkboxes como marcados quando o modal abre
  useEffect(() => {
    if (open && activeFamilyKey && families[currentBrand]?.[activeFamilyKey]) {
      const fam = families[currentBrand][activeFamilyKey];
      const allCells = new Set();
      fam.variants.forEach((variant) => {
        fam.products.forEach((prod) => {
          const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
          allCells.add(compoundId);
        });
      });
      setSelectedMatrixCells(allCells);
    }
  }, [open, activeFamilyKey, currentBrand, families]);

  if (!activeFamilyKey) return null;

  const fam = families[currentBrand][activeFamilyKey];

  const toggleMatrixCheckbox = (compoundId) => {
    const newSet = new Set(selectedMatrixCells);
    if (newSet.has(compoundId)) {
      newSet.delete(compoundId);
    } else {
      newSet.add(compoundId);
    }
    setSelectedMatrixCells(newSet);
  };

  const getColorHex = (name) => {
    const map = {
      Branco: "#FFFFFF",
      Bianco: "#FFFFFF",
      Bege: "#F5F5DC",
      Nocciola: "#D2B48C",
      "Azul Naval": "#000080",
      Notte: "#191970",
      "Cinza Steel": "#778899",
      Marmo: "#E0E0E0",
      "Verde Eucalipto": "#556B2F",
      Argento: "#C0C0C0",
      "Rosa Malva": "#D8BFD8",
      Nero: "#000000",
      Rose: "#FF007F",
      Grafite: "#333333",
      Taupe: "#483C32",
      Natural: "#F0E68C",
      Vermelho: "#FF0000",
      "Bianco/Marinho": "#000080",
      "Bianco/Caqui": "#F0E68C",
    };
    return map[name] || "#eee";
  };

  const handleApplyBulk = () => {
    const qty = parseInt(bulkQty);
    if (!isNaN(qty) && qty >= 0) {
      const newTempState = { ...tempGridState };
      
      fam.variants.forEach((variant) => {
        fam.products.forEach((prod) => {
          const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
          const colorMatch =
            selectedBulkColors.size === 0 ||
            selectedBulkColors.has(variant);
          const productMatch =
            selectedBulkProducts.size === 0 ||
            selectedBulkProducts.has(prod.id);
          const isCheckboxSelected = selectedMatrixCells.has(compoundId);

          if (colorMatch && productMatch && isCheckboxSelected) {
            const multiple = prod.multiple || 1;
            let adjustedQty = qty;
            if (adjustedQty > 0) {
              const remainder = adjustedQty % multiple;
              if (remainder !== 0) {
                adjustedQty = adjustedQty + (multiple - remainder);
              }
            }
            newTempState[compoundId] = adjustedQty;
          }
        });
      });

      setTempGridState(newTempState);
      setBulkQty("");
    }
  };

  const getColorLabel = () => {
    if (selectedBulkColors.size === 0) return "Todas as Cores";
    if (selectedBulkColors.size === 1) {
      const color = Array.from(selectedBulkColors)[0];
      return color.length > 20 ? color.substring(0, 18) + "..." : color;
    }
    return `${selectedBulkColors.size} Selecionados`;
  };

  const getProductLabel = () => {
    if (selectedBulkProducts.size === 0) return "Todos os Produtos";
    if (selectedBulkProducts.size === 1) {
      const prodId = Array.from(selectedBulkProducts)[0];
      const prod = fam.products.find((p) => p.id === prodId);
      const name = prod ? prod.name : prodId;
      return name.length > 20 ? name.substring(0, 18) + "..." : name;
    }
    return `${selectedBulkProducts.size} Selecionados`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="grid-modal-dialog"
      PaperProps={{
        className: "grid-modal-paper",
      }}
    >
      <DialogTitle className="grid-modal-header">
        <Box className="grid-modal-header-left">
          <Box>
            <Typography 
              className="grid-modal-title brand-font"
              style={{ color: theme.primary }}
            >
              {fam.name}
            </Typography>
            <Typography className="grid-modal-subtitle">Selecione variantes</Typography>
          </Box>
          <Button onClick={onClose} className="close-btn">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Box className="grid-modal-toolbar">
        <Typography 
          className="toolbar-label"
          style={{ color: theme.primary }}
        >
          Massa:
        </Typography>
        <Box 
          className="bulk-qty-input-wrapper"
          onFocus={(e) => {
            const wrapper = e.currentTarget;
            if (wrapper) {
              wrapper.style.boxShadow = `0 0 0 1px ${theme.primary}`;
              wrapper.style.borderColor = theme.primary;
            }
          }}
          onBlur={(e) => {
            const wrapper = e.currentTarget;
            if (wrapper && !wrapper.contains(document.activeElement)) {
              wrapper.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
              wrapper.style.borderColor = '#d1d5db';
            }
          }}
        >
          <Typography className="bulk-qty-label">Qtd</Typography>
          <TextField
            type="number"
            value={bulkQty}
            onChange={(e) => setBulkQty(e.target.value)}
            placeholder="0"
            className="bulk-qty-input"
            variant="outlined"
            fullWidth
            InputProps={{
              className: "bulk-qty-input-inner",
              notched: false,
            }}
            inputProps={{
              style: { height: '100%', width: '80px' },
            }}
            style={{
              border: 'none',
              background: 'none',
              boxShadow: 'none',
              height: '100%',
              width: '80px',
            }}
          />
        </Box>
        <Box className="toolbar-divider" />
        <Box className="custom-select-wrapper">
          <Box
            className="custom-select-btn"
            onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
            style={{
              borderColor: colorDropdownOpen ? theme.primary : undefined,
              boxShadow: colorDropdownOpen ? `0 0 0 1px ${theme.primary}` : undefined,
            }}
          >
            <Typography>{getColorLabel()}</Typography>
            <ExpandMoreIcon />
          </Box>
          {colorDropdownOpen && (
            <Box className="custom-select-list">
              {fam.variants.map((v) => (
                <Box
                  key={v}
                  className={`custom-select-item ${
                    selectedBulkColors.has(v) ? "selected" : ""
                  }`}
                  onClick={() => toggleBulkSelection("color", v)}
                  style={selectedBulkColors.has(v) ? {
                    backgroundColor: "#eff6ff",
                    color: theme.primary,
                  } : undefined}
                >
                  <Checkbox
                    checked={selectedBulkColors.has(v)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                  <Typography>{v}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box className="custom-select-wrapper">
          <Box
            className="custom-select-btn"
            onClick={() => setProductDropdownOpen(!productDropdownOpen)}
            style={{
              borderColor: productDropdownOpen ? theme.primary : undefined,
              boxShadow: productDropdownOpen ? `0 0 0 1px ${theme.primary}` : undefined,
            }}
          >
            <Typography>{getProductLabel()}</Typography>
            <ExpandMoreIcon />
          </Box>
          {productDropdownOpen && (
            <Box className="custom-select-list">
              {fam.products.map((p) => (
                <Box
                  key={p.id}
                  className={`custom-select-item ${
                    selectedBulkProducts.has(p.id) ? "selected" : ""
                  }`}
                  onClick={() => toggleBulkSelection("prod", p.id)}
                  style={selectedBulkProducts.has(p.id) ? {
                    backgroundColor: "#eff6ff",
                    color: theme.primary,
                  } : undefined}
                >
                  <Checkbox
                    checked={selectedBulkProducts.has(p.id)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                  <Typography>{p.name}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Button 
          className="apply-bulk-btn" 
          onClick={handleApplyBulk}
          style={{ backgroundColor: theme.primary }}
        >
          Aplicar
        </Button>

        <Box className="toolbar-info">
          <InfoIcon 
            className="info-icon" 
            style={{ color: theme.primary }}
          />
          <Typography className="info-text">
            Afeta apenas itens{" "}
            <Box 
              component="span" 
              className="checkmark-icon"
              style={{ backgroundColor: theme.primary }}
            >
              ✓
            </Box>{" "}
            marcados.
          </Typography>
        </Box>
      </Box>

      <DialogContent className="grid-modal-content">
        <Table className="matrix-table">
          <TableHead className="matrix-header">
            <TableRow>
              <TableCell className="matrix-header-col sticky">Cor / Variante</TableCell>
              {fam.products.map((prod) => {
                const finalPrice =
                  prod.price * (1 - (prod.discount || 0) / 100);
                const hasDiscount = prod.discount > 0;

                return (
                  <TableCell key={prod.id} className="matrix-header-col">
                    <Box className="card-status-badge status-badge status-A">{fam.status}</Box>
                    <Typography className="matrix-prod-name">{prod.name}</Typography>
                    <Typography className="matrix-prod-ref">Ref: {prod.ref}</Typography>
                    {hasDiscount ? (
                      <Box className="matrix-price-container">
                        <Typography className="matrix-price-old">
                          R$ {MaskAndValid.maskDecimal(prod.price, 0)}
                        </Typography>
                        <Typography className="matrix-price-new">
                          R$ {MaskAndValid.maskDecimal(finalPrice, 0)}
                        </Typography>
                        <Box className="matrix-discount-badge">
                          -{prod.discount}%
                        </Box>
                      </Box>
                    ) : (
                      <Typography 
                        className="matrix-price"
                        style={{ color: theme.primary }}
                      >
                        R$ {MaskAndValid.maskDecimal(prod.price, 0)}
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {fam.variants.map((variant) => (
              <TableRow key={variant} className="matrix-row">
                <TableCell className="matrix-variant-cell sticky">
                  <Box className="variant-cell-content">
                    <Box
                      className="color-dot"
                      style={{ backgroundColor: getColorHex(variant) }}
                    />
                    <Typography className="variant-name">{variant}</Typography>
                  </Box>
                </TableCell>
                {fam.products.map((prod) => {
                  const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
                  const val = tempGridState[compoundId] || "";
                  const multiple = prod.multiple || 1;
                  const multipleText = multiple > 1 ? `Múlt: ${multiple}` : "-";
                  const isChecked = selectedMatrixCells.has(compoundId);

                  return (
                    <TableCell key={prod.id} className="matrix-input-cell">
                      <Box className="matrix-input-wrapper">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => toggleMatrixCheckbox(compoundId)}
                          className="matrix-checkbox b2b-checkbox"
                          style={{ color: theme.primary }}
                        />
                        <TextField
                          type="number"
                          value={val}
                          onChange={(e) => {
                            const value = e.target.value;
                            let intVal = parseInt(value);
                            const newTempState = { ...tempGridState };

                            if (intVal > 0) {
                              const remainder = intVal % multiple;
                              if (remainder !== 0) {
                                intVal = intVal + (multiple - remainder);
                              }
                              newTempState[compoundId] = intVal;
                            } else {
                              delete newTempState[compoundId];
                            }

                            setTempGridState(newTempState);
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = `2px solid ${theme.primary}`;
                          }}
                          onBlur={(e) => {
                            e.target.style.outline = 'none';
                          }}
                          placeholder={multipleText}
                          className="matrix-input"
                          min="0"
                          step={multiple}
                          InputProps={{
                            className: "matrix-input-inner",
                          }}
                        />
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions className="grid-modal-actions">
        <Button onClick={onClose} className="cancel-btn">
          Cancelar
        </Button>
        <Button
          onClick={onSave}
          className="save-btn"
          style={{
            backgroundColor: theme.primary,
          }}
          startIcon={<SaveIcon />}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
